// src/components/ContactForm.js
import { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { API_BASE } from "../utils/env";

export default function ContactForm() {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState({ ok: false, msg: "" });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "Website Contact",
    message: "",
    sendCopy: true, // customer copy
    honeypot: "", // hidden anti-bot field
  });

  const onChange = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setResult({ ok: false, msg: "" });

    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const contentType = res.headers.get("content-type") || "";
      let data = null;

      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        throw new Error(
          `Unexpected response: ${res.status} ${text.slice(0, 200)}`
        );
      }

      if (!res.ok) {
        throw new Error(data?.error || "send_failed");
      }

      setResult({ ok: true, msg: "Message sent! We’ll reply shortly." });
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "Website Contact",
        message: "",
        sendCopy: true,
        honeypot: "",
      });
    } catch (err) {
      setResult({
        ok: false,
        msg: err?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      {/* Honeypot: hidden field bots will fill, humans won't */}
      <input
        type="text"
        name="website"
        value={form.honeypot}
        onChange={onChange("honeypot")}
        style={{ display: "none" }}
        autoComplete="off"
        tabIndex={-1}
      />

      <Grid
        container
        sx={{
          maxWidth: 600,
          mx: "auto",
          mt: 4,
          p: 3,
          backgroundColor: "#FFF3E4",
          borderRadius: 2,
        }}
      >
        <TextField
          fullWidth
          label="Name"
          margin="normal"
          required
          value={form.name}
          onChange={onChange("name")}
        />
        <TextField
          fullWidth
          label="Email"
          type="email"
          margin="normal"
          required
          value={form.email}
          onChange={onChange("email")}
        />
        <TextField
          fullWidth
          label="Phone (Optional)"
          margin="normal"
          value={form.phone}
          onChange={onChange("phone")}
        />
        <TextField
          fullWidth
          label="Message"
          margin="normal"
          multiline
          rows={4}
          required
          value={form.message}
          onChange={onChange("message")}
        />

        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox checked={form.sendCopy} onChange={onChange("sendCopy")} />
          }
          label="Email me a copy"
        />

        {result.msg && (
          <Alert
            sx={{ mt: 2, width: "100%" }}
            severity={result.ok ? "success" : "error"}
          >
            {result.msg}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 2, bgcolor: "black" }}
          disabled={sending}
          startIcon={sending ? <CircularProgress size={18} /> : null}
        >
          {sending ? "Sending..." : "Send Message"}
        </Button>
      </Grid>
    </form>
  );
}
